import React, { useMemo, useState } from "react";
import {
  LayoutDashboard, Library, Upload, Inbox, ShieldCheck, Users, MapPinned, Bus, BarChart3, Settings,
} from "lucide-react";
import { makeClient, useMeta } from "./api";
import { Spinner } from "./ui";
import { Biblioteca } from "./Biblioteca";
import { ContenidoForm } from "./ContenidoForm";
import { EnviosList } from "./EnviosList";
import { Colaboradores } from "./Colaboradores";
import { Localidades } from "./Localidades";
import { Gira } from "./Gira";
import { Stats, KpiGrid, useStats } from "./Stats";
import { Config } from "./Config";

const MENU = [
  { id: "panel", label: "Panel general", icon: LayoutDashboard },
  { id: "biblioteca", label: "Biblioteca multimedia", icon: Library },
  { id: "cargar", label: "Cargar contenidos", icon: Upload },
  { id: "recibidos", label: "Videos recibidos", icon: Inbox },
  { id: "moderacion", label: "Moderación y aprobaciones", icon: ShieldCheck },
  { id: "colaboradores", label: "Colaboradores", icon: Users },
  { id: "localidades", label: "Provincias y localidades", icon: MapPinned },
  { id: "gira", label: "Gira de la Esperanza", icon: Bus },
  { id: "stats", label: "Estadísticas", icon: BarChart3 },
  { id: "config", label: "Configuración", icon: Settings },
];

const Panel = ({ client, go }) => {
  const s = useStats(client, 0);
  if (!s) return <Spinner />;
  return (
    <div className="space-y-6" data-testid="red-panel">
      <KpiGrid s={s} />
      <div className="grid md:grid-cols-3 gap-4">
        {[
          ["moderacion", `Revisar ${s.envios.pendiente} envío(s) pendiente(s)`, "Moderación y aprobaciones"],
          ["cargar", "Subir videos, fotos y afiches", "Cargar contenidos"],
          ["colaboradores", "Aprobar cuentas nuevas y asignar moderadores", "Colaboradores"],
        ].map(([id, txt, t]) => (
          <button key={id} onClick={() => go(id)} className="text-left bg-[#0A192F] text-white p-5 hover:bg-[#0A2463] transition-colors" data-testid={`panel-go-${id}`}>
            <div className="font-headings uppercase tracking-wide text-[#F5B400] text-sm">{t}</div>
            <div className="mt-1 text-sm text-white/80">{txt}</div>
          </button>
        ))}
      </div>
      <div className="bg-white border border-gray-200 p-5 text-sm text-[#374151]">
        <p><strong>Portal de colaboradores:</strong> <span className="font-mono">{window.location.origin}/#/colaboradores</span> — compartí este enlace para que se registren. Las cuentas nuevas quedan pendientes hasta que las apruebes.</p>
      </div>
    </div>
  );
};

export const RedAdmin = ({ adminKey }) => {
  const client = useMemo(() => makeClient({ adminKey }), [adminKey]);
  const [meta, reloadMeta] = useMeta();
  const [sec, setSec] = useState("panel");
  const [refresh, setRefresh] = useState(0);

  const view = {
    panel: <Panel client={client} go={setSec} />,
    biblioteca: <Biblioteca client={client} meta={meta} isAdmin refreshKey={refresh} />,
    cargar: <ContenidoForm client={client} meta={meta} onCreated={() => setRefresh((x) => x + 1)} />,
    recibidos: <EnviosList client={client} meta={meta} testid="red-recibidos" />,
    moderacion: <EnviosList client={client} meta={meta} defaultEstado="pendiente" testid="red-moderacion" />,
    colaboradores: <Colaboradores client={client} meta={meta} />,
    localidades: <Localidades client={client} meta={meta} onChange={reloadMeta} />,
    gira: <Gira client={client} meta={meta} />,
    stats: <Stats client={client} />,
    config: <Config client={client} />,
  }[sec];

  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-6" data-testid="red-admin">
      <nav className="lg:sticky lg:top-4 self-start bg-white border border-gray-200 flex lg:flex-col overflow-x-auto" data-testid="red-menu">
        <div className="hidden lg:block px-4 pt-4 pb-2 font-headings uppercase tracking-[0.2em] text-[11px] text-[#D97706]">Red de contenidos</div>
        {MENU.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setSec(id)}
            className={`flex items-center gap-2.5 px-4 py-2.5 text-sm whitespace-nowrap text-left border-b-2 lg:border-b-0 lg:border-l-2 transition-colors ${
              sec === id ? "border-[#F5B400] bg-[#0A2463]/5 text-[#0A2463] font-semibold" : "border-transparent text-[#4B5563] hover:text-[#0A192F] hover:bg-gray-50"
            }`}
            data-testid={`red-menu-${id}`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </nav>
      <section className="min-w-0">
        <h2 className="font-headings uppercase tracking-tight text-2xl text-[#0A192F] mb-5" data-testid="red-section-title">
          {MENU.find((m) => m.id === sec).label}
        </h2>
        {view}
      </section>
    </div>
  );
};

export default RedAdmin;
